import { useEffect } from 'react';
import Flex from 'components/ui/Flex';
import Input from 'components/ui/Input';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';
import { useTranslation } from 'react-i18next';
import { ReturnOrderLine } from 'returnCenter-api';
import { misPickSkuKey, returnSelectors, returnThunks } from 'store/reducers/returnSlice';
import styled from 'styled-components';

/** How long typing has to pause before the SKU is checked against the catalog. */
const LOOKUP_DELAY_MS = 400;

type MisPickSkuCellProps = {
    orderline: ReturnOrderLine;
    /** SKU of the ordered line this item was delivered in place of. */
    parentSku?: string | null;
    /** The ordered line has nothing in the return, which blocks saving while this line has a quantity. */
    parentLacksQuantity?: boolean;
    onSkuChange: (sku: string) => void;
    disabled?: boolean;
    itemId?: number | string;
    rowIndex?: number;
    colIndex?: number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: React.KeyboardEvent, itemId: string | number, rowIndex: number, colIndex: number) => void;
    cellRefPrefix?: string;
};

const isSameSku = (a?: string | null, b?: string | null) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * The SKU of a mis-pick line: the item that was actually delivered. It starts as the ordered line's SKU and is
 * changed by the user; completing the return is blocked while it is empty, still the ordered one, or not in the
 * catalog. The catalog check runs when typing pauses and when the field is left.
 */
const MisPickSkuCell = ({
    orderline,
    parentSku,
    parentLacksQuantity = false,
    onSkuChange,
    disabled = false,
    itemId,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
}: MisPickSkuCellProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const sku = orderline.sku ?? '';
    const needsChange = !sku.trim() || isSameSku(sku, parentSku);
    const check = useAppSelector(returnSelectors.selectMisPickSkuChecks)[misPickSkuKey(sku)];
    const notFound = !needsChange && check?.status === 'notFound';

    useEffect(() => {
        if (disabled || needsChange) return;
        const timer = setTimeout(() => dispatch(returnThunks.lookupMisPickSku(sku)), LOOKUP_DELAY_MS);
        return () => clearTimeout(timer);
    }, [disabled, dispatch, needsChange, sku]);

    return (
        <Container column gap="4px" $grow={1}>
            <Text as="label" xBold fontSize={11}>
                {t('misPick.label', { defaultValue: 'Mis-picked item' })}
            </Text>
            {disabled ? (
                <Text as="label" xBold fontSize={11}>
                    {t('SKU')}: {sku || '-'}
                </Text>
            ) : (
                <StyledInput
                    data-allow-paste
                    data-input
                    data-row={rowIndex}
                    data-row-cell={colIndex}
                    data-item-id={itemId}
                    key={itemId}
                    placeholder={t('SKU')}
                    warning={needsChange || notFound}
                    ref={(el) => {
                        if (cellInputRefs && rowIndex !== undefined && colIndex !== undefined) {
                            cellInputRefs.current[`${cellRefPrefix}${rowIndex}-${colIndex}`] = el;
                        }
                    }}
                    onChange={(event) => onSkuChange(event.currentTarget.value)}
                    onBlur={() => {
                        if (!needsChange) dispatch(returnThunks.lookupMisPickSku(sku));
                    }}
                    onKeyDown={(e) => {
                        if (handleArrowKeyDown && rowIndex !== undefined && colIndex !== undefined) {
                            handleArrowKeyDown(e, itemId ?? -1, rowIndex, colIndex);
                        }
                    }}
                    value={sku}
                />
            )}
            {!needsChange && check?.status === 'checking' && <Spinner size="small" useFullHeight={false} />}
            {notFound && (
                <Text as="label" fontSize={11} color="error">
                    {t('misPick.skuNotFound', { defaultValue: 'Not in the catalog' })}
                </Text>
            )}
            {parentLacksQuantity && (
                <Text as="label" fontSize={11} color="error">
                    {t('misPick.parentNoQuantity', { defaultValue: 'The ordered item has no quantity' })}
                </Text>
            )}
            {orderline.productName && (
                <Text bold as="label" fontSize={11}>
                    {orderline.skuName ? `${orderline.productName}, ${orderline.skuName}` : orderline.productName}
                </Text>
            )}
            {parentSku && (
                <Text as="label" fontSize={11}>
                    {t('misPick.insteadOf', { defaultValue: 'Instead of {{sku}}', sku: parentSku })}
                </Text>
            )}
        </Container>
    );
};

export default MisPickSkuCell;

const Container = styled(Flex)`
    overflow-wrap: anywhere;
    margin-bottom: 0.5rem;
`;

const StyledInput = styled(Input)`
    width: 100%;
    input {
        border-radius: 4px;
        height: 32px !important;
        padding-left: 10px !important;
    }

    > * {
        font-size: 11px !important;
    }
`;
